import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCityPrice } from "../../hooks/useCityPrice";
import { isServiceEnabled } from "../../config";
import { OUTDOOR_SERVICES } from "../../config/pricing";
import CategorySidebar from "./CategorySidebar";

import gardening_img from "../../components/images/stage_01/gardening.png";
import vehicle_washing_img from "../../components/images/stage_01/vehicle_washing.png";
import outdoor_hero_img from "../../components/images/stage_01/car_spacing_cleaning_169.png";
// Explore Other Services — local assets, matching the same category
// thumbnails already used for these cross-links on the other listing pages
// (e.g. TransportServices.js's own extraServices).
import complete_house_cleaning_img from "../../components/images/stage_01/complete_house_cleaning.png";
import electrical_img from "../../components/images/stage_01/electrical.png";
import elder_caretaker_img from "../../components/images/stage_01/elder_caretaker_R2.png";
import packers_movers_img from "../../components/images/stage_01/complete_packers_movers.png";
import special_service_img from "../../components/images/stage_01/special_service.png";
import deep_cleaning_img from "../../components/images/stage_01/deep_cleaning.png";

const fallbackImage =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";


export const services = [
  {
    id: "os-1",
    serviceKey: "gardening",
    name: "Gardening Service",
    desc: "Plant care, watering, trimming and routine garden maintenance.",
    ...OUTDOOR_SERVICES["os-1"],
    image: gardening_img,
    images: [
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?auto=format&fit=crop&w=900&q=80",
    ],
    color: "#22c55e",
    link: "/services/outdoor-services/gardening",
    includes: [
      "Watering of all plants and flower beds",
      "Trimming and pruning of overgrown plants",
      "Weed removal from garden beds",
      "Fertilizer application (if provided by owner)",
      "Basic garden tidying and leaf clearing",
    ],
    excludes: [
      "Purchase of plants, soil or fertilizers",
      "Tree cutting or large branch removal",
      "Landscape redesign or planting layout",
      "Pest control spraying for plants",
    ],
  },
  {
    id: "os-6",
    serviceKey: "carwash",
    name: "Car Wash at Home",
    desc: "Exterior wash, tyre cleaning and basic interior dust cleanup.",
    ...OUTDOOR_SERVICES["os-6"],
    image: vehicle_washing_img,
    images: [
      "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80",
    ],
    color: "#6366f1",
    link: "/services/outdoor-services/carwash",
    includes: [
      "Full exterior body wash and rinse",
      "Tyre and rim cleaning",
      "Interior dashboard and seat dust wipe",
      "Window glass interior and exterior wipe",
      "Floor mat shake-out and basic vacuum",
    ],
    excludes: [
      "Engine cleaning or mechanical work",
      "Polishing, waxing or paint protection",
      "Scratch removal or dent repair",
      "Deep interior shampoo or steam cleaning",
    ],
  },
];

const extraServices = [
  { name: "Home Services",      link: "/services/instant-home-services", image: complete_house_cleaning_img },
  { name: "Technical Services", link: "/services/technical-services",    image: electrical_img },
  { name: "Caretaker Services", link: "/services/caretaker-services",    image: elder_caretaker_img },
  { name: "Transport Services", link: "/services/transport-services",    image: packers_movers_img },
  { name: "Special Services",   link: "/services/special-services",      image: special_service_img },
  { name: "Deep Cleaning",      link: "/service/special-service/deep-cleaning", image: deep_cleaning_img },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const OutdoorServices = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();

  // A service the Dashboard's Enable/Disable tab turns off is left out of
  // this list entirely, not shown disabled.
  const visibleServices = services.filter((service) => isServiceEnabled(service.id));

  return (
    <div style={styles.page}>
      <div style={styles.layout}>

        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <CategorySidebar />

          <div style={styles.helpCard}>
            <h4 style={styles.helpTitle}>Need Help with Outdoor Work?</h4>
            <p style={styles.helpText}>Book reliable outdoor experts for cleaning and maintenance.</p>
            <Link to="/contact-us" style={{ ...styles.chatBtn, display: "inline-block", textDecoration: "none", textAlign: "center" }}>💬 Chat with Expert</Link>
            <div style={styles.helpIcon}>🌿</div>
          </div>
        </aside>

        {/* Main */}
        <main style={styles.main}>
          <div style={styles.breadcrumb}>Home &gt; Outdoor Services</div>

          {/* Hero */}
          <div style={styles.heroCard}>
            <div style={styles.heroContent}>
              <h1 style={styles.heroTitle}>Outdoor Services</h1>
              <p style={styles.heroText}>Garden care and vehicle cleaning experts, right at your doorstep</p>
              <div style={styles.heroFeatures}>
                <span style={styles.heroFeature}>Verified Professionals</span>
                <span style={styles.heroFeature}>On-time Service</span>
                <span style={styles.heroFeature}>Transparent Pricing</span>
                <span style={styles.heroFeature}>Satisfaction Guarantee</span>
              </div>
            </div>
            <img
              src={outdoor_hero_img}
              alt="outdoor services"
              style={styles.heroImage}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
            />
          </div>

          {/* Section header */}
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Popular Outdoor Services</h2>
            <span style={styles.serviceCount}>Showing {visibleServices.length} services</span>
          </div>

          {/* Service cards */}
          <div style={styles.serviceGrid}>
            {visibleServices.map((service) => (
              <div key={service.id} style={styles.serviceCard}>
                <img
                  src={service.image}
                  alt={service.name}
                  style={styles.serviceImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
                />

                <div style={styles.serviceBody}>
                  <div style={{ ...styles.serviceIcon, background: `${service.color}20`, color: service.color }}>
                    ✦
                  </div>
                  <h3 style={styles.serviceName}>{service.name}</h3>
                  <p style={styles.serviceDesc}>{service.desc}</p>

                  <div style={styles.serviceMeta}>
                    <span style={styles.price}>₹{cityPrice(service.id, service.price)}</span>
                    <span style={styles.duration}>{service.duration}</span>
                  </div>

                  {/* Buttons row */}
                  <div style={styles.btnRow}>
                    <button style={styles.bookBtn} onClick={() => navigate(service.link)}>
                      Book Now
                    </button>
                    <button
                      style={styles.detailsBtn}
                      onClick={() => navigate(service.link)}
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* See our professionals at work */}
          <h3 style={styles.gallerySectionTitle}>See our professionals at work</h3>
          <div style={styles.galleryGrid}>
            <div style={styles.galleryCard}>
              <img src={gardening_img} alt="Garden maintenance" style={styles.galleryImage}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
              <div style={styles.galleryCaption}>Garden maintenance</div>
            </div>
            <div style={styles.galleryCard}>
              <img src={vehicle_washing_img} alt="Car wash at home" style={styles.galleryImage}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
              <div style={styles.galleryCaption}>Car wash at home</div>
            </div>
          </div>

          {/* You may also need */}
          <div style={styles.exploreSection}>
            <h3 style={styles.exploreTitle}>You may also need</h3>
            <div style={styles.exploreGrid}>
              {extraServices.map((item, index) => (
                <Link key={index} to={item.link} style={styles.exploreCardLink}>
                  <div style={styles.exploreCard}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={styles.exploreImage}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
                    />
                    <div style={styles.exploreCardBody}>
                      <h4 style={styles.exploreName}>{item.name}</h4>
                      <span style={styles.exploreLink}>Check Now →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Banner — Bronze Package (membership subscription upsell) */}
          <div style={styles.bannerCard}>
            <img
              src={complete_house_cleaning_img}
              alt="Bronze Package"
              style={styles.bannerImage}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
            />
            <div style={styles.bannerContent}>
              <span style={styles.bannerBadge}>Subscription Plan</span>
              <h3 style={styles.bannerTitle}>Bronze Package</h3>
              <p style={styles.bannerText}>A monthly home &amp; outdoor care subscription — more visits, more savings</p>
              <ul style={styles.bannerList}>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> 26 Service Visits / Month (2 hrs 15 min each)</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> 2 Partners — Flexible Slot Timings</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> Choose 1, 3, 6 or 12 Month Plans</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> Free Product Kit on Every Tier</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> Max Discount on Services</li>
              </ul>
            </div>
            <div style={styles.bannerAction}>
              <p style={styles.saveText}>Flexible Monthly Plans</p>
              <Link to="/subscription-plans/checkout/silver-package" style={styles.bookNowBtnLink}>Book Now →</Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ─── Page styles ──────────────────────────────────────────────────────────────
const styles = {
  page:             { background: "#f8fafc", minHeight: "100vh", padding: "20px", fontFamily: "Arial, sans-serif", color: "#111827" },
  layout:           { maxWidth: "1300px", margin: "0 auto", display: "grid", gridTemplateColumns: "250px 1fr", gap: "20px", alignItems: "start" },
  sidebar:          { display: "flex", flexDirection: "column", gap: "18px" },
  helpCard:         { background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "18px", padding: "18px", textAlign: "left" },
  helpTitle:        { margin: "0 0 8px", fontSize: "18px", fontWeight: 700 },
  helpText:         { margin: "0 0 14px", fontSize: "14px", color: "#6b7280", lineHeight: 1.6 },
  chatBtn:          { border: "none", background: "#fff", color: "#4f46e5", fontWeight: 700, borderRadius: "12px", padding: "12px 16px", cursor: "pointer" },
  helpIcon:         { fontSize: "54px", textAlign: "center", marginTop: "18px" },
  main:             { minWidth: 0 },
  breadcrumb:       { fontSize: "13px", color: "#9ca3af", marginBottom: "14px" },
  heroCard:         { display: "grid", gridTemplateColumns: "1.2fr 0.9fr", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "22px", overflow: "hidden", marginBottom: "22px" },
  heroContent:      { padding: "26px" },
  heroTitle:        { margin: 0, fontSize: "42px", fontWeight: 800 },
  heroText:         { margin: "10px 0 18px", fontSize: "15px", color: "#6b7280" },
  heroFeatures:     { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" },
  heroFeature:      { fontSize: "13px", color: "#4b5563", background: "#f8fafc", border: "1px solid #eef2f7", padding: "10px 12px", borderRadius: "12px", fontWeight: 600 },
  // Source photo is a near-3:2 collage, so a 3/2 box crops it only slightly.
  // car_spacing_cleaning_169 is natively 16:9 — match it exactly so the
  // hero shows the whole photo instead of cropping it to the old 3:2 box.
  heroImage:        { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", objectPosition: "center", display: "block" },
  sectionHeader:    { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  sectionTitle:     { margin: 0, fontSize: "24px", fontWeight: 700 },
  serviceCount:     { fontSize: "14px", color: "#6b7280" },
  serviceGrid:      { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px" },
  serviceCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", position: "relative" },
  qtyBadge:         { position: "absolute", top: "10px", right: "10px", background: "#4f46e5", color: "#fff", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "999px" },
  serviceImage:     { width: "100%", aspectRatio: "4 / 3", objectFit: "cover" },
  serviceBody:      { padding: "12px" },
  serviceIcon:      { width: "26px", height: "26px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "8px", fontSize: "13px", fontWeight: 700 },
  serviceName:      { margin: "0 0 6px", fontSize: "15px", fontWeight: 700 },
  serviceDesc:      { margin: "0 0 10px", fontSize: "12px", lineHeight: 1.5, color: "#6b7280", minHeight: "48px" },
  serviceMeta:      { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  price:            { fontSize: "17px", fontWeight: 800 },
  duration:         { fontSize: "11.5px", color: "#6b7280", fontWeight: 600 },
  btnRow:           { display: "grid", gridTemplateColumns: "1fr auto", gap: "6px" },
  bookBtn:          { height: "36px", border: "none", background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", borderRadius: "10px", fontWeight: 700, fontSize: "12.5px", cursor: "pointer" },
  bookBtnBooked:    { background: "linear-gradient(135deg,#16a34a,#15803d)" },
  detailsBtn:       { height: "36px", padding: "0 10px", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#374151", borderRadius: "10px", fontWeight: 700, fontSize: "12.5px", cursor: "pointer", whiteSpace: "nowrap" },
  gallerySectionTitle: { margin: "32px 0 16px", fontSize: "24px", fontWeight: 800 },
  galleryGrid:      { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "18px" },
  galleryCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", overflow: "hidden" },
  galleryImage:     { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" },
  galleryCaption:   { padding: "14px 16px", fontWeight: 700, fontSize: "15px" },
  // Bold gradient treatment (vs. the plain white card every other section
  // uses) so this subscription upsell reads as a distinct, higher-stakes
  // offer rather than just another info card.
  bannerCard:       { marginTop: "22px", background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)", borderRadius: "20px", padding: "22px", display: "grid", gridTemplateColumns: "220px 1fr auto", gap: "24px", alignItems: "center", boxShadow: "0 14px 34px rgba(67,56,202,0.3)" },
  bannerImage:      { width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: "16px", display: "block", boxShadow: "0 6px 18px rgba(0,0,0,0.25)" },
  bannerContent:    {},
  bannerBadge:      { display: "inline-block", background: "rgba(255,255,255,0.18)", color: "#fff", fontSize: "11px", fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", padding: "5px 12px", borderRadius: "999px", marginBottom: "10px" },
  bannerTitle:      { margin: "0 0 6px", fontSize: "26px", fontWeight: 800, color: "#fff" },
  bannerText:       { margin: "0 0 14px", fontSize: "14px", color: "rgba(255,255,255,0.85)" },
  bannerTags:       { display: "flex", gap: "10px", flexWrap: "wrap", fontSize: "13px", color: "#4b5563" },
  bannerList:       { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" },
  bannerListItem:   { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: "#fff" },
  bannerCheckIcon:  { color: "#86efac", fontWeight: 900, flexShrink: 0 },
  bannerAction:     { textAlign: "center" },
  saveText:         { margin: "0 0 12px", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "rgba(255,255,255,0.8)" },
  bookNowBtn:       { border: "none", background: "#4f46e5", color: "#fff", height: "44px", padding: "0 18px", borderRadius: "12px", fontWeight: 700, cursor: "pointer" },
  bookNowBtnLink:   { display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", background: "#fff", color: "#4338ca", height: "50px", padding: "0 26px", borderRadius: "12px", fontWeight: 800, fontSize: "14.5px", cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap", boxShadow: "0 6px 16px rgba(0,0,0,0.2)" },
  exploreSection:   { marginTop: "32px" },
  exploreTitle:     { margin: "0 0 14px", fontSize: "24px", fontWeight: 700 },
  exploreGrid:      { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "12px" },
  exploreCardLink:  { textDecoration: "none", color: "inherit" },
  exploreCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", textAlign: "left" },
  exploreImage:     { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f3f4f6" },
  exploreCardBody:  { padding: "14px" },
  exploreName:      { margin: "0 0 10px", fontSize: "15px", fontWeight: 700 },
  exploreLink:      { color: "#4f46e5", fontWeight: 700, fontSize: "13px" },
};

export default OutdoorServices;
